/**
 * SubmittedStep — success screen after the application is submitted.
 *
 * Shows the estimated review time and links to Home / Marketplace.
 * `clearApplicationId()` is called by the WizardFlow (not here) so
 * the localStorage entry is cleaned up before this screen renders.
 */
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { SELLER_REG_NS } from "@/features/seller-registration/api";

const STEP_H1_CLASS =
  "text-[27px] font-bold leading-tight tracking-[-0.03em] text-foreground";

export interface SubmittedStepProps {
  estimatedReviewHours: number | null;
}

export function SubmittedStep({
  estimatedReviewHours,
}: SubmittedStepProps): ReactElement {
  const { t } = useTranslation(SELLER_REG_NS);
  const navigate = useNavigate();

  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-8 text-center">
      {/* Icon */}
      <div
        className={[
          "grid h-20 w-20 place-items-center rounded-full",
          "bg-[var(--g-surface-raised)] text-4xl",
        ].join(" ")}
        aria-hidden="true"
      >
        ✅
      </div>

      {/* Copy */}
      <div className="flex flex-col gap-3">
        <h1 className={STEP_H1_CLASS} data-testid="submitted-h1">
          {t("submitted.h1")}
        </h1>
        <p className="text-sm text-muted-foreground">
          {estimatedReviewHours != null
            ? t("submitted.lede", { hours: estimatedReviewHours })
            : t("submitted.ledeDefault")}
        </p>
      </div>

      {/* Actions */}
      <div className="flex w-full max-w-xs flex-col gap-3">
        <button
          type="button"
          onClick={() => navigate("/marketplace")}
          data-testid="submitted-go-marketplace"
          className={[
            "flex h-[44px] w-full items-center justify-center",
            "rounded-[var(--g-radius)] bg-primary px-4 text-sm font-semibold",
            "text-[var(--g-on-primary)] shadow-[var(--g-shadow-sm)]",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          ].join(" ")}
        >
          {t("submitted.goMarketplace")}
        </button>
        <button
          type="button"
          onClick={() => navigate("/")}
          data-testid="submitted-go-home"
          className={[
            "flex h-[44px] w-full items-center justify-center",
            "rounded-[var(--g-radius)] border border-[var(--g-divider)]",
            "bg-[var(--g-surface)] px-4 text-sm font-medium text-foreground",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          ].join(" ")}
        >
          {t("submitted.goHome")}
        </button>
      </div>
    </div>
  );
}
